import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Image, Save, Plus, Trash2, Eye, EyeOff, Megaphone, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAssets() {
  const [assets, setAssets] = useState([]);
  const [banners, setBanners] = useState([]);
  const [tab, setTab] = useState("assets");
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({ image_url: "", label: "" });
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({ title: "", image_url: "", link: "", page: "all", enabled: false });
  const [loading, setLoading] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const bannerFileRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, bRes] = await Promise.all([API.get("/admin/game-assets"), API.get("/admin/banners")]);
        setAssets(aRes.data.assets);
        setBanners(bRes.data.banners);
      } catch {}
    };
    load();
  }, []);

  const saveAsset = async (assetId) => {
    setLoading(`asset-${assetId}`);
    try {
      await API.put(`/admin/game-assets/${assetId}`, assetForm);
      toast.success("Asset updated");
      const { data } = await API.get("/admin/game-assets");
      setAssets(data.assets);
      setEditingAsset(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const uploadFile = async (file, target) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await API.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND_URL}${data.url}`;
      if (target === "asset") {
        setAssetForm(prev => ({ ...prev, image_url: url }));
      } else {
        setBannerForm(prev => ({ ...prev, image_url: url }));
      }
      toast.success("Image uploaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const createBanner = async () => {
    setLoading("create-banner");
    try {
      await API.post("/admin/banners", bannerForm);
      toast.success("Banner created");
      const { data } = await API.get("/admin/banners");
      setBanners(data.banners);
      setBannerForm({ title: "", image_url: "", link: "", page: "all", enabled: false });
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const saveBanner = async (bannerId) => {
    setLoading(`banner-${bannerId}`);
    try {
      await API.put(`/admin/banners/${bannerId}`, bannerForm);
      toast.success("Banner updated");
      const { data } = await API.get("/admin/banners");
      setBanners(data.banners);
      setEditingBanner(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const deleteBanner = async (bannerId) => {
    try {
      await API.delete(`/admin/banners/${bannerId}`);
      toast.success("Banner deleted");
      setBanners(prev => prev.filter(b => b.banner_id !== bannerId));
    } catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout title="Assets & Banners">
      <div className="space-y-6" data-testid="admin-assets">
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {["assets", "banners"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`assets-tab-${t}`}
              className={`flex-1 py-2 text-sm font-bold uppercase tracking-widest rounded-md transition-all ${tab === t ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              {t === "assets" ? "Game Images" : "Ad Banners"}
            </button>
          ))}
        </div>

        {tab === "assets" && (
          <div className="space-y-3">
            <p className="text-sm text-[#94A3B8]">Replace default game images with your own. Changes appear across the app.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <div key={asset.asset_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`asset-card-${asset.asset_id}`}>
                  <div className="h-32 bg-[#0B0E14] flex items-center justify-center">
                    {asset.image_url ? (
                      <img src={asset.image_url} alt={asset.label} className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-8 h-8 text-[#64748B]" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-mono-game text-[#64748B] mb-1">{asset.asset_id}</p>
                    <p className="text-sm font-medium text-[#F8FAFC] mb-2">{asset.label}</p>
                    {editingAsset === asset.asset_id ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input value={assetForm.image_url} onChange={(e) => setAssetForm({ ...assetForm, image_url: e.target.value })} placeholder="Image URL"
                            className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-xs flex-1" />
                          <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "asset")} />
                          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid={`upload-asset-${asset.asset_id}`}
                            className="px-3 py-1 text-xs font-bold uppercase bg-[#B026FF]/20 text-[#B026FF] border border-[#B026FF]/30 rounded-lg hover:bg-[#B026FF]/30 disabled:opacity-50 whitespace-nowrap">
                            <Upload className="w-3 h-3 inline mr-1" />{uploading ? "..." : "Upload"}
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => saveAsset(asset.asset_id)} disabled={loading === `asset-${asset.asset_id}`} data-testid={`save-asset-${asset.asset_id}`}
                            className="flex-1 py-1.5 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">
                            <Save className="w-3 h-3 inline mr-1" /> Save
                          </button>
                          <button onClick={() => setEditingAsset(null)} className="px-3 py-1.5 text-xs text-[#94A3B8] border border-[#2A3241] rounded-lg">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingAsset(asset.asset_id); setAssetForm({ image_url: asset.image_url || "", label: asset.label || "" }); }}
                        data-testid={`edit-asset-${asset.asset_id}`} className="w-full py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">
                        Replace Image
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "banners" && (
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">Create ad banners for any page. All banners are disabled by default.</p>

            {/* Create new banner */}
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 space-y-3" data-testid="create-banner-form">
              <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2"><Plus className="w-4 h-4 text-[#00F0FF]" /> New Banner</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Title</Label>
                  <Input value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="Ad title"
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Image URL</Label>
                  <Input value={bannerForm.image_url} onChange={(e) => setBannerForm({ ...bannerForm, image_url: e.target.value })} placeholder="https://..."
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Link</Label>
                  <Input value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} placeholder="https://..."
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Page</Label>
                  <select value={bannerForm.page} onChange={(e) => setBannerForm({ ...bannerForm, page: e.target.value })}
                    className="w-full h-8 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-2 text-sm">
                    <option value="all">All Pages</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="mining">Extraction</option>
                    <option value="inventory">Inventory</option>
                    <option value="wallet">Wallet</option>
                    <option value="tasks">Tasks</option>
                  </select>
                </div>
              </div>
              <button onClick={createBanner} disabled={loading === "create-banner"} data-testid="create-banner-btn"
                className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
                {loading === "create-banner" ? "Creating..." : "Create Banner (Disabled by Default)"}
              </button>
            </div>

            {/* Existing banners */}
            <div className="space-y-2">
              {banners.map((banner) => (
                <div key={banner.banner_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-4" data-testid={`banner-${banner.banner_id}`}>
                  {banner.image_url ? (
                    <img src={banner.image_url} alt="" className="w-20 h-12 rounded object-cover border border-[#2A3241]" />
                  ) : (
                    <div className="w-20 h-12 rounded bg-[#0B0E14] flex items-center justify-center"><Megaphone className="w-4 h-4 text-[#64748B]" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#F8FAFC] truncate">{banner.title || "Untitled"}</p>
                    <p className="text-xs text-[#64748B]">Page: {banner.page} | {banner.enabled ? "Active" : "Disabled"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingBanner === banner.banner_id ? (
                      <button onClick={() => saveBanner(banner.banner_id)} disabled={loading === `banner-${banner.banner_id}`}
                        className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded"><Save className="w-4 h-4" /></button>
                    ) : (
                      <button onClick={() => {
                        setEditingBanner(banner.banner_id);
                        setBannerForm({ title: banner.title, image_url: banner.image_url, link: banner.link, page: banner.page, enabled: !banner.enabled });
                      }} data-testid={`toggle-banner-${banner.banner_id}`}
                        className={`p-1.5 rounded ${banner.enabled ? "text-[#10B981] hover:bg-[#10B981]/10" : "text-[#64748B] hover:bg-[#64748B]/10"}`}>
                        {banner.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={() => deleteBanner(banner.banner_id)} data-testid={`delete-banner-${banner.banner_id}`}
                      className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {banners.length === 0 && <p className="text-[#94A3B8] text-center py-6">No banners created yet. They&apos;re disabled by default.</p>}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
